import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, idem, unwrap } from "../client";
import { qk } from "../keys";
import type { QueryOf } from "../types";
import type { SafetyAcknowledgeBody, SpendCapUpdateBody } from "./types";

export function useSafetyReport(project: string, environment?: string) {
  return useQuery({
    queryKey: qk.safety(project, environment),
    queryFn: () =>
      unwrap(
        api.GET("/v1/projects/{project}/safety", {
          params: { path: { project }, query: environment ? { environment } : {} },
        }),
      ),
    enabled: !!project,
  });
}

export function useRunSafetyScan(project: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => unwrap(api.POST("/v1/projects/{project}/safety/scan", { params: { path: { project }, header: idem() } })),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["project", project, "safety"] }),
  });
}

export function useApplySafetyFix(project: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (checkId: string) =>
      unwrap(
        api.POST("/v1/projects/{project}/safety/checks/{check_id}/fix", {
          params: { path: { project, check_id: checkId }, header: idem() },
        }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["project", project, "safety"] }),
  });
}

export function useAcknowledgeSafetyCheck(project: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ checkId, body }: { checkId: string; body: SafetyAcknowledgeBody }) =>
      unwrap(
        api.POST("/v1/projects/{project}/safety/checks/{check_id}/acknowledge", {
          params: { path: { project, check_id: checkId }, header: idem() },
          body,
        }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["project", project, "safety"] }),
  });
}

/** Public, unauthenticated badge summary (the growth-loop page). */
export function usePublicSafetySummary(badgeId: string) {
  return useQuery({
    queryKey: qk.publicSafety(badgeId),
    queryFn: () =>
      unwrap(api.GET("/v1/public/safety/{badge_id}", { params: { path: { badge_id: badgeId } } })),
    enabled: !!badgeId,
  });
}

export function useProjectSpend(project: string, filter: { month?: string; environment?: string } = {}) {
  return useQuery({
    queryKey: qk.projectSpend(project, filter),
    queryFn: () =>
      unwrap(api.GET("/v1/projects/{project}/spend", { params: { path: { project }, query: filter } })),
    enabled: !!project,
  });
}

export function useProjectUsage(project: string, filter: QueryOf<"getProjectUsage">) {
  return useQuery({
    queryKey: qk.usage(project, filter),
    queryFn: () =>
      unwrap(api.GET("/v1/projects/{project}/usage", { params: { path: { project }, query: filter } })),
    enabled: !!project,
  });
}

export function useWorkspaceSpend(workspace: string) {
  return useQuery({
    queryKey: qk.workspaceSpend(workspace),
    queryFn: () => unwrap(api.GET("/v1/workspaces/{workspace}/spend", { params: { path: { workspace } } })),
    enabled: !!workspace,
  });
}

export function useSpendCaps(workspace: string) {
  return useQuery({
    queryKey: qk.spendCaps(workspace),
    queryFn: () => unwrap(api.GET("/v1/workspaces/{workspace}/spend-caps", { params: { path: { workspace } } })),
    enabled: !!workspace,
  });
}

/** Raising a cap is a dangerous action: wrap the call in <ConfirmTyped> (docs/11 §4). */
export function useUpdateSpendCap(workspace: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ capId, body }: { capId: string; body: SpendCapUpdateBody }) =>
      unwrap(
        api.PATCH("/v1/workspaces/{workspace}/spend-caps/{cap_id}", {
          params: { path: { workspace, cap_id: capId }, header: idem() },
          body,
        }),
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.workspace(workspace) });
      void qc.invalidateQueries({ queryKey: ["project"] });
    },
  });
}

export function useBilling(workspace: string) {
  return useQuery({
    queryKey: qk.billing(workspace),
    queryFn: () => unwrap(api.GET("/v1/workspaces/{workspace}/billing", { params: { path: { workspace } } })),
    enabled: !!workspace,
  });
}

export function useInvoices(workspace: string) {
  return useQuery({
    queryKey: qk.invoices(workspace),
    queryFn: () => unwrap(api.GET("/v1/workspaces/{workspace}/invoices", { params: { path: { workspace } } })),
    enabled: !!workspace,
  });
}

export function useRateCard() {
  return useQuery({
    queryKey: qk.rateCard(),
    queryFn: () => unwrap(api.GET("/v1/rate-card")),
    staleTime: 60 * 60_000,
  });
}
